import MappDataSourceLogo from '@/assets/icons/mapp-logo.svg?react';
import StripeLogo from '@/assets/icons/stripe-logo.svg?react';
import { Loadable } from '@/components/loaders';
import { clearIntegrationData } from '@/features/cyclr/dataSourceIntegrationSlice';
import { ConnectorInReviewModal } from '@/features/destinations/commons';
import { resetUploadStatus } from '@/features/fileUploading/fileUploadingSlice';
import { SupportModal } from '@/features/layout/Sidebars/common/components/SidebarBase/SidebarNavigation/Items/SidebarSupportItem/SupportModal';
import RouteConstants from '@/features/router/RouteConstants';
import { clearStashedRules } from '@/features/rules/rulesSlice';
import { SupportSuccessModal } from '@/features/support';
import { useDelayedBoolean } from '@/hooks/useDelayedBoolean';
import { useSlideOverContext } from '@/hooks/useSlideOverContext';
import { DataSourceType } from '@/models/dataSource';
import { EStatus } from '@/models/fileUpload';
import { useAppDispatch, useAppSelector } from '@/reducers';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { DataSourceTypePanelV2 } from '..';
import { clearStashedDataSource } from '../../dataSourcesSlice';
import { DataSourceComingSoonCard, SourceAwareCatalogGrid } from '../commons';
import { SourceConnectorAuthSlideOver } from '../slideOver_v2/SourceConnectorAuthSlideOver';
import { SourceConnectorManageSlideOver } from '../slideOver_v2/SourceConnectorManageSlideOver';
import { SourceConnectorWizardRouteState } from '../createDataSource/createDataSourceContent/dataSourceTypes/sourceConnector/types';
import { SourceConnectorCatalogItem, useSourceConnectorCatalogItems } from '../sourceConnectors/useSourceConnectorCatalogItems';

export const DataSourcesCatalog: FC = () => {
  const { t } = useTranslation();

  const { openSlideOver, closeSlideOver } = useSlideOverContext();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const { standardTypes, sourceConnectorItems, isLoading } = useSourceConnectorCatalogItems();
  const isFetchingDelayed = useDelayedBoolean(isLoading, 400);

  const fileUploadStatus = useAppSelector((state) => state.fileUploading.status);

  const [showedModal, setShowedModal] = useState<'default' | 'connectorInReview' | 'supportSuccess' | null>(null);

  const closeModal = () => setShowedModal(null);
  const showSupportModal = () => setShowedModal('default');
  const showConnectorInReviewModal = () => setShowedModal('connectorInReview');
  const showSupportSuccessModal = () => setShowedModal('supportSuccess');

  const isSupportModalVisible = showedModal === 'default';
  const isConnectorInReviewModalVisible = showedModal === 'connectorInReview';
  const isSupportSuccessModalVisible = showedModal === 'supportSuccess';

  const showSlideOver = (dataSourceType: DataSourceType) => {
    if (dataSourceType.name === 'Salesforce') {
      showConnectorInReviewModal();
      return;
    }

    openSlideOver({
      renderContent: DataSourceTypePanelV2,
      renderContentProps: { dataSourceType, onAddDataSourceClicked: openDataSourceCreatePage },
      showButtonInsideSlideOver: true,
    });
  };

  const openConnectSlideOver = (item: SourceConnectorCatalogItem) => {
    openSlideOver({
      renderContent: SourceConnectorAuthSlideOver,
      renderContentProps: { item },
      showButtonInsideSlideOver: true,
    });
  };

  const openManageSlideOver = (item: SourceConnectorCatalogItem) => {
    openSlideOver({
      renderContent: SourceConnectorManageSlideOver,
      renderContentProps: {
        item,
        onAddDataSource: () => openJourneyBFromConnector(item),
      },
      showButtonInsideSlideOver: true,
    });
  };

  const openJourneyBFromConnector = (item: SourceConnectorCatalogItem) => {
    clearStore();
    closeSlideOver();
    const routeState: SourceConnectorWizardRouteState = {
      connectorId: item.connector.id,
      fromCatalogManage: true,
    };
    history.push({
      pathname: RouteConstants.dataSourcesCreate.replace(':id', item.dataSourceType.id),
      state: routeState,
    });
  };

  const clearStore = () => {
    if (fileUploadStatus?.uploadingStatus !== EStatus.UPLOADING) {
      dispatch(resetUploadStatus());
    }

    dispatch(clearStashedDataSource());
    dispatch(clearStashedRules());
    dispatch(clearIntegrationData());
  };

  const openDataSourceCreatePage = (dataSourceType: DataSourceType) => {
    clearStore();
    closeSlideOver();
    history.push(RouteConstants.dataSourcesCreate.replace(':id', dataSourceType.id));
  };

  return (
    <Loadable isLoading={isFetchingDelayed}>
      <div className='flex flex-col gap-6'>
        <p className='text-sm font-semibold text-secondary-700'>{t('data_sources:catalog_subtitle')}</p>

        <SourceAwareCatalogGrid
          standardTypes={standardTypes}
          sourceConnectorItems={sourceConnectorItems}
          onStandardTypeClick={showSlideOver}
          onConnect={openConnectSlideOver}
          onManage={openManageSlideOver}
        >
          <DataSourceComingSoonCard icon={() => <MappDataSourceLogo />} title='mapp' />
          <DataSourceComingSoonCard icon={() => <StripeLogo />} title='stripe' />
        </SourceAwareCatalogGrid>
      </div>
      {isConnectorInReviewModalVisible && <ConnectorInReviewModal onConfirm={async () => showSupportModal()} onClose={closeModal} />}
      {isSupportModalVisible && <SupportModal onConfirm={showSupportSuccessModal} onClose={closeModal} />}
      {isSupportSuccessModalVisible && <SupportSuccessModal onConfirm={async () => closeModal()} onClose={closeModal} />}
    </Loadable>
  );
};
